/**
 * Drag and drop from a finger.
 *
 * Phones have no native HTML5 drag: iOS never starts one from a touch and Android only after a long press, and
 * not in every browser. This turns a finger on a `draggable` element into the same sequence of drag events a
 * mouse produces (dragstart, dragenter, dragover, dragleave, drop, dragend), dispatched on the same elements with
 * the same coordinates, so every surface keeps its one set of handlers and never knows the difference.
 *
 * A drag starts after a short press that stays still (a swipe scrolls as always), or at once from a drag handle
 * (`.grip`, `.handle`: the stylesheet gives those `touch-action: none`, so the browser never scrolls from them).
 * While it lasts a copy of the dragged element follows the finger, the page does not scroll under it except near
 * the top or bottom edge of its scroller, and the tap that ends it is not a click. A native drag the browser
 * starts on its own from the same press is cancelled, so nothing happens twice.
 *
 * Installed once, from main.tsx. The pure parts (the data transfer, the edge scrolling) are unit-tested.
 */

/** A finger that stays still this long on a draggable element starts a drag. */
export const PRESS_MS = 300;
/** Movement before that is a scroll, not a drag. */
export const SLOP = 8;
/** Distance from the scroller's top or bottom edge within which the drag scrolls it. */
export const EDGE = 64;
/** The most the scroller moves per tick, in px. */
export const MAX_SCROLL = 18;
/** How often the drag looks under a still finger (the scroller may have moved) and scrolls at the edge. */
const TICK_MS = 40;
const HANDLE = '.grip, .handle';
const NOT_FROM = 'input, textarea, select, button, a, label, [contenteditable=""], [contenteditable="true"]';

const typeOf = (fmt: string) => {
  const f = fmt.toLowerCase();
  return f === 'text' ? 'text/plain' : f === 'url' ? 'text/uri-list' : f;
};

/** The dataTransfer of the synthetic drag events: what the handlers read and write of the real one. */
export class TouchDataTransfer {
  dropEffect: 'none' | 'copy' | 'link' | 'move' = 'none';
  effectAllowed = 'uninitialized';
  readonly files: File[] = [];
  readonly items: never[] = [];
  /** What setDragImage asked for: the element to follow the finger and the point of it under the finger. */
  dragImage: { el: Element; x: number; y: number } | null = null;
  private readonly data = new Map<string, string>();
  get types(): string[] {
    return Array.from(this.data.keys());
  }
  setData(fmt: string, value: string): void {
    this.data.set(typeOf(fmt), String(value));
  }
  getData(fmt: string): string {
    return this.data.get(typeOf(fmt)) ?? '';
  }
  clearData(fmt?: string): void {
    if (fmt === undefined) this.data.clear();
    else this.data.delete(typeOf(fmt));
  }
  setDragImage(el: Element, x: number, y: number): void {
    this.dragImage = { el, x, y };
  }
}

/**
 * How far the scroller moves this tick for a finger at `y`: nothing in the middle, up to MAX_SCROLL at the
 * very edge, growing linearly across the EDGE band. Negative scrolls up.
 */
export function edgeScroll(y: number, top: number, bottom: number, edge = EDGE, max = MAX_SCROLL): number {
  if (bottom - top < edge * 2) edge = Math.max(8, Math.floor((bottom - top) / 2));
  if (y < top + edge) return -Math.ceil(Math.min(1, (top + edge - y) / edge) * max);
  if (y > bottom - edge) return Math.ceil(Math.min(1, (y - (bottom - edge)) / edge) * max);
  return 0;
}

interface Pending {
  id: number;
  x: number;
  y: number;
  target: Element;
  /** The finger is on a drag handle: the drag starts on the first movement, no press needed. */
  handle: boolean;
  timer: number;
}

interface Active {
  id: number;
  source: HTMLElement;
  dt: TouchDataTransfer;
  ghost: { root: HTMLElement; dx: number; dy: number } | null;
  scroller: HTMLElement | null;
  x: number;
  y: number;
  /** The element under the finger last frame, which got the dragenter. */
  over: Element | null;
  /** The last dragover on it was cancelled: a drop is accepted there. */
  canDrop: boolean;
  timer: number;
  lastOver: number;
}

let pending: Pending | null = null;
let active: Active | null = null;
let blockClicksUntil = 0;
let installed = false;

function dragEvent(type: string, x: number, y: number, dt: TouchDataTransfer, cancelable: boolean, relatedTarget: Element | null = null): Event {
  const init: DragEventInit = { bubbles: true, cancelable, composed: true, clientX: x, clientY: y, screenX: x, screenY: y, relatedTarget, view: window };
  let ev: Event;
  try {
    ev = new DragEvent(type, init);
  } catch {
    ev = new MouseEvent(type, init);
  }
  // own property: shadows the prototype's getter, which would give null (or throw) for a scripted event
  Object.defineProperty(ev, 'dataTransfer', { value: dt, configurable: true });
  return ev;
}

/** The nearest ancestor that scrolls vertically, or null for the window. */
function scrollerOf(el: Element): HTMLElement | null {
  for (let p = el.parentElement; p && p !== document.body; p = p.parentElement) {
    const s = getComputedStyle(p);
    if (/(auto|scroll)/.test(s.overflowY) && p.scrollHeight > p.clientHeight + 1) return p;
  }
  return null;
}

const WRAP_RESET =
  'position:static;margin:0;padding:0;border:0;min-width:0;min-height:0;max-width:none;max-height:none;width:auto;height:auto;' +
  'background:transparent;box-shadow:none;overflow:visible;transform:none;animation:none;transition:none;opacity:1;outline:0;' +
  'border-radius:0;inset:auto;float:none;columns:auto;list-style:none;';
const DISPLAY: Record<string, string> = { TABLE: 'table', TBODY: 'table-row-group', THEAD: 'table-header-group', TFOOT: 'table-footer-group' };

/**
 * The copy that follows the finger: the element setDragImage named, else the source itself, cloned inside shallow
 * clones of its ancestors (same tags and classes, every layout style reset) so it keeps the colours, the variables
 * and the descendant rules of where it came from, and fixed on the page where nothing can hit-test it.
 */
function makeGhost(source: HTMLElement, dt: TouchDataTransfer, x: number, y: number): Active['ghost'] {
  const img = dt.dragImage && dt.dragImage.el.isConnected && dt.dragImage.el.getBoundingClientRect().width > 0 ? dt.dragImage : null;
  const el = (img?.el ?? source) as HTMLElement;
  const rect = el.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) return null;
  const clone = el.cloneNode(true) as HTMLElement;
  clone.removeAttribute('id');
  clone.querySelectorAll('[id]').forEach((n) => n.removeAttribute('id'));
  clone.querySelectorAll('[tabindex]').forEach((n) => n.removeAttribute('tabindex'));
  clone.classList.remove('dragging');
  clone.style.cssText += `;margin:0;box-sizing:border-box;width:${rect.width}px;height:${rect.height}px;transform:none;transition:none;animation:none;opacity:0.92;`;
  if (el instanceof HTMLTableRowElement && clone instanceof HTMLTableRowElement) {
    Array.from(el.cells).forEach((c, i) => {
      const cc = clone.cells[i];
      if (cc) cc.style.cssText += `;box-sizing:border-box;width:${c.getBoundingClientRect().width}px;`;
    });
  } else {
    clone.style.boxShadow = '0 10px 28px rgba(0, 0, 0, 0.35)';
  }
  let inner: HTMLElement = clone;
  let root: HTMLElement = clone;
  for (let anc = el.parentElement; anc && anc !== document.body; anc = anc.parentElement) {
    const w = anc.cloneNode(false) as HTMLElement;
    w.removeAttribute('id');
    w.removeAttribute('tabindex');
    w.style.cssText += `;${WRAP_RESET}display:${DISPLAY[anc.tagName] ?? 'block'};`;
    if (anc.tagName === 'TABLE') w.style.cssText += `width:${rect.width}px;table-layout:fixed;border-collapse:collapse;`;
    w.appendChild(inner);
    inner = w;
    root = w;
  }
  root.style.cssText += `;position:fixed;left:0;top:0;z-index:9999;pointer-events:none;width:${rect.width}px;will-change:transform;`;
  root.setAttribute('data-touch-ghost', '');
  root.setAttribute('aria-hidden', 'true');
  const dx = img ? img.x : x - rect.left;
  const dy = img ? img.y : y - rect.top;
  root.style.transform = `translate3d(${x - dx}px, ${y - dy}px, 0)`;
  document.body.appendChild(root);
  return { root, dx, dy };
}

function clearPending(): void {
  if (pending) window.clearTimeout(pending.timer);
  pending = null;
}

/** The press held, or the handle moved: the drag begins, unless the dragstart handler refuses it. */
function begin(p: Pending): Active | null {
  clearPending();
  const source = p.target.closest<HTMLElement>('[draggable="true"]');
  if (!source) return null;
  const dt = new TouchDataTransfer();
  if (!source.dispatchEvent(dragEvent('dragstart', p.x, p.y, dt, true))) return null;
  blockClicksUntil = Number.MAX_SAFE_INTEGER; // released when the drag ends
  navigator.vibrate?.(12);
  active = { id: p.id, source, dt, ghost: makeGhost(source, dt, p.x, p.y), scroller: scrollerOf(source), x: p.x, y: p.y, over: null, canDrop: false, timer: 0, lastOver: 0 };
  // the first dragover comes on the first movement or the first tick, after the dragstart handler's state has rendered
  active.timer = window.setInterval(tick, TICK_MS);
  return active;
}

/** The element under the finger gets dragenter/dragleave when it changes and dragover on every look. */
function updateOver(a: Active): void {
  a.lastOver = performance.now();
  const el = document.elementFromPoint(a.x, a.y);
  if (el !== a.over) {
    if (a.over) a.over.dispatchEvent(dragEvent('dragleave', a.x, a.y, a.dt, false, el));
    if (el) el.dispatchEvent(dragEvent('dragenter', a.x, a.y, a.dt, true, a.over));
    a.over = el;
  }
  a.canDrop = el ? !el.dispatchEvent(dragEvent('dragover', a.x, a.y, a.dt, true)) : false;
}

function place(a: Active): void {
  if (a.ghost) a.ghost.root.style.transform = `translate3d(${a.x - a.ghost.dx}px, ${a.y - a.ghost.dy}px, 0)`;
}

/** The finger moved: the copy follows it and what is under it hears about it (at most once a frame). */
function moved(a: Active, x: number, y: number): void {
  a.x = x;
  a.y = y;
  place(a);
  if (performance.now() - a.lastOver >= 16) updateOver(a);
}

/** Every tick: near the edge of the scroller the page moves under the still finger (the drag has taken the touch scrolling). */
function tick(): void {
  const a = active;
  if (!a) return;
  let top = 0;
  let bottom = window.innerHeight;
  if (a.scroller) {
    const r = a.scroller.getBoundingClientRect();
    top = Math.max(0, r.top);
    bottom = Math.min(window.innerHeight, r.bottom);
  } else {
    const header = document.querySelector<HTMLElement>('.header');
    if (header && getComputedStyle(header).position === 'sticky') top = Math.max(0, header.getBoundingClientRect().bottom);
  }
  const dy = edgeScroll(a.y, top, bottom);
  if (dy !== 0) {
    if (a.scroller) a.scroller.scrollTop += dy;
    else window.scrollBy(0, dy);
  }
  updateOver(a);
}

/** The finger lifted (a drop where the last dragover accepted one) or the browser took the touch (no drop). */
function finish(drop: boolean): void {
  const a = active;
  if (!a) return;
  active = null;
  window.clearInterval(a.timer);
  blockClicksUntil = Date.now() + 400; // the tap that ends a drag is not a click
  // a moment later: the handlers' state from the last dragover has rendered by then, as it has with a mouse
  window.setTimeout(() => {
    if (drop && a.canDrop && a.over) a.over.dispatchEvent(dragEvent('drop', a.x, a.y, a.dt, true));
    else a.dt.dropEffect = 'none';
    a.source.dispatchEvent(dragEvent('dragend', a.x, a.y, a.dt, false));
    a.ghost?.root.remove();
  }, 20);
}

const touchLike = (e: PointerEvent) => e.pointerType !== 'mouse';

function onPointerDown(e: PointerEvent): void {
  if (!touchLike(e) || !e.isPrimary || active) return;
  clearPending();
  const target = e.target instanceof Element ? e.target : null;
  if (!target || !target.closest('[draggable]')) return;
  const handle = target.closest(HANDLE) !== null;
  if (!handle && target.closest(NOT_FROM)) return;
  const p: Pending = { id: e.pointerId, x: e.clientX, y: e.clientY, target, handle, timer: 0 };
  p.timer = window.setTimeout(() => pending === p && begin(p), PRESS_MS);
  pending = p;
}

function onPointerMove(e: PointerEvent): void {
  if (active) {
    if (e.pointerId === active.id) moved(active, e.clientX, e.clientY);
    return;
  }
  if (!pending || e.pointerId !== pending.id) return;
  const dist = Math.hypot(e.clientX - pending.x, e.clientY - pending.y);
  if (pending.handle) {
    if (dist > 3) {
      const a = begin(pending);
      if (a) moved(a, e.clientX, e.clientY);
    }
  } else if (dist > SLOP) clearPending(); // a scroll
}

function onPointerUp(e: PointerEvent): void {
  if (active && e.pointerId === active.id) finish(true);
  else if (pending && e.pointerId === pending.id) clearPending();
}

function onPointerCancel(e: PointerEvent): void {
  if (active && e.pointerId === active.id) finish(false);
  else if (pending && e.pointerId === pending.id) clearPending();
}

function onTouchMove(e: TouchEvent): void {
  if (!active) return;
  if (e.cancelable) e.preventDefault(); // the drag owns the touch: no scrolling under it
  else finish(false); // the browser is already scrolling: too late for a drag
}

function onContextMenu(e: Event): void {
  if (pending || active) e.preventDefault(); // Android's long-press menu
}

function onClick(e: MouseEvent): void {
  if (Date.now() >= blockClicksUntil) return;
  e.stopPropagation();
  e.preventDefault();
}

/** The browser's own touch drag from the same press (Android, some iPads) would run alongside ours: cancel it. */
function onNativeDragStart(e: DragEvent): void {
  if (!e.isTrusted || !(pending || active)) return;
  e.preventDefault();
  e.stopImmediatePropagation();
}

export function installTouchDrag(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  const cap = { capture: true } as const;
  document.addEventListener('pointerdown', onPointerDown, cap);
  document.addEventListener('pointermove', onPointerMove, cap);
  document.addEventListener('pointerup', onPointerUp, cap);
  document.addEventListener('pointercancel', onPointerCancel, cap);
  document.addEventListener('touchmove', onTouchMove, { capture: true, passive: false });
  document.addEventListener('contextmenu', onContextMenu, cap);
  document.addEventListener('click', onClick, cap);
  document.addEventListener('dragstart', onNativeDragStart, cap);
  window.addEventListener('pagehide', () => {
    clearPending();
    finish(false);
  });
}

/** For the tests and the console: is a finger drag running. */
export const touchDragActive = () => active !== null;
